import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { LOGOUT } from '../../constants/actions/auth.constants';
import AdminUsersService from '../services/AdminUsersService';
import type { AppState } from '../../reducers/root.reducer';
import { createSelector } from 'reselect';
import IPaginationState from '../../types/state/IPaginationState';
import IAdminUser from '../types/users/IAdminUser';
import UploadState from '../../types/UploadState';
import IBaseState from '../../types/IBaseState';
import Report from '../types/Report';
import AdminUserProject from '../types/users/AdminUserProject';
import ImportStates, { ImportState } from '../constants/ImportStates';
import ImportProcess from '../../common/types/ImportProcess';
import ImportService from '../services/ImportService';

const NAME = 'AdminUsers';

const getUsers = createAsyncThunk(
    `${NAME}/getUsers`,
    AdminUsersService.getUsers,
);
const createUser = createAsyncThunk(
    `${NAME}/createUser`,
    AdminUsersService.createUser,
);
const updateUser = createAsyncThunk(
    `${NAME}/updateUser`,
    AdminUsersService.updateUser,
);
const deleteUser = createAsyncThunk(
    `${NAME}/deleteUser`,
    AdminUsersService.deleteUser,
);
const getUser = createAsyncThunk(`${NAME}/getUser`, AdminUsersService.getUser);
const getReports = createAsyncThunk(
    `${NAME}/getReports`,
    AdminUsersService.getReports,
);
const getJobMatchReports = createAsyncThunk(
    `${NAME}/getJobMatchReports`,
    AdminUsersService.getJobMatchReports,
);
const getProjects = createAsyncThunk(
    `${NAME}/getProjects`,
    AdminUsersService.getProjects,
);
const getManagedProjects = createAsyncThunk(
    `${NAME}/getManagedProjects`,
    AdminUsersService.getManagedProjects,
);
const archive = createAsyncThunk(`${NAME}/archive`, AdminUsersService.archive);
const anonymize = createAsyncThunk(
    `${NAME}/anonymize`,
    AdminUsersService.anonymize,
);
const resetAuth = createAsyncThunk(
    `${NAME}/resetAuth`,
    AdminUsersService.resetAuth,
);
const createJobMatch = createAsyncThunk(
    `${NAME}/createJobMatch`,
    AdminUsersService.createJobMatch,
);
const importUsers = createAsyncThunk(
    `${NAME}/importUsers`,
    AdminUsersService.importUsers,
);
const confirmImport = createAsyncThunk(
    `${NAME}/confirmImport`,
    ImportService.confirmImport,
);
const cancelImport = createAsyncThunk(
    `${NAME}/cancelImport`,
    ImportService.cancelImport,
);
const getImportProgress = createAsyncThunk(
    `${NAME}/getImportProgress`,
    ImportService.getImportProgress,
);
const exportUsers = createAsyncThunk(
    `${NAME}/exportUsers`,
    AdminUsersService.exportUsers,
);
const getImportTemplate = createAsyncThunk(
    `${NAME}/getImportTemplate`,
    AdminUsersService.getImportTemplate,
);

export interface AdminUsersState {
    users: IPaginationState<IAdminUser[]>;
    detail: UploadState<IAdminUser | undefined>;
    reports: IBaseState<Report[]>;
    jobMatchReports: IBaseState<Report[]>;
    projects: IBaseState<AdminUserProject[]>;
    managedProjects: IBaseState<AdminUserProject[]>;
    import: {
        loading: boolean;
        preview: any | null;
        process: ImportProcess | null;
        state: ImportState;
        importId: number | null;
        userId: number | null;
    };
}

const initialState: AdminUsersState = {
    users: {
        data: [],
        loading: false,
        pagination: { page: 1 },
    },
    detail: {
        data: undefined,
        loading: false,
        uploading: false,
    },
    reports: { data: [], loading: false },
    jobMatchReports: { data: [], loading: false },
    projects: { data: [], loading: false },
    managedProjects: { data: [], loading: false },
    import: {
        loading: false,
        preview: null,
        process: null,
        state: ImportStates.Idle,
        importId: ImportService.getImportId(),
        userId: null,
    },
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {
        resetImport,
    },
    extraReducers: (builder) => {
        builder.addCase(
            getUsers.fulfilled,
            (state: AdminUsersState, action) => {
                state.users.data = action.payload.data;
                state.users.pagination = (action.payload as any).pagination;
            },
        );
        builder.addCase(
            createUser.fulfilled,
            (state: AdminUsersState, action) => {
                state.users.data.push(action.payload.data);
            },
        );
        builder.addCase(
            updateUser.fulfilled,
            (state: AdminUsersState, action) => {
                const id = action.meta.arg.userId;
                const index = state.users.data.findIndex(
                    (item) => item.id === id,
                );

                if (index >= 0) {
                    state.users.data[index] = {
                        ...state.users.data[index],
                        ...action.meta.arg,
                    };
                }
            },
        );
        builder.addCase(
            deleteUser.fulfilled,
            (state: AdminUsersState, action) => {
                const id = action.meta.arg;
                const index = state.users.data.findIndex(
                    (item) => item.id === id,
                );

                if (index >= 0) {
                    state.users.data.splice(index, 1);
                }
            },
        );

        builder.addCase(getUser.pending, (state: AdminUsersState) => {
            state.detail.loading = true;
        });
        builder.addCase(getUser.fulfilled, (state: AdminUsersState, action) => {
            state.detail.data = action.payload.data;
        });

        builder.addCase(getReports.pending, (state: AdminUsersState) => {
            state.reports.loading = true;
        });
        builder.addCase(
            getReports.fulfilled,
            (state: AdminUsersState, action) => {
                state.reports.data = action.payload.data;
            },
        );
        builder.addCase(getReports.rejected, (state: AdminUsersState) => {
            state.reports.data = [];
        });
        builder.addCase(
            getJobMatchReports.pending,
            (state: AdminUsersState) => {
                state.jobMatchReports.loading = true;
            },
        );
        builder.addCase(
            getJobMatchReports.fulfilled,
            (state: AdminUsersState, action) => {
                state.jobMatchReports.data = action.payload.data;
            },
        );
        builder.addCase(
            getJobMatchReports.rejected,
            (state: AdminUsersState) => {
                state.jobMatchReports.data = [];
            },
        );

        builder.addCase(getProjects.pending, (state: AdminUsersState) => {
            state.projects.loading = true;
        });
        builder.addCase(
            getProjects.fulfilled,
            (state: AdminUsersState, action) => {
                state.projects.data = action.payload.data;
            },
        );
        builder.addCase(getProjects.rejected, (state: AdminUsersState) => {
            state.projects.data = [];
        });
        builder.addCase(
            getManagedProjects.pending,
            (state: AdminUsersState) => {
                state.managedProjects.loading = true;
            },
        );
        builder.addCase(
            getManagedProjects.fulfilled,
            (state: AdminUsersState, action) => {
                state.managedProjects.data = action.payload.data;
            },
        );
        builder.addCase(
            getManagedProjects.rejected,
            (state: AdminUsersState) => {
                state.managedProjects.data = [];
            },
        );

        builder.addCase(importUsers.pending, (state: AdminUsersState) => {
            resetImport(state);
            state.import.state = ImportStates.Pending;
        });
        builder.addCase(
            importUsers.fulfilled,
            (state: AdminUsersState, action) => {
                state.import.state = ImportStates.Preview;
                state.import.importId = action.payload.data.importId;
                state.import.preview = action.payload.data.preview;
            },
        );
        builder.addCase(importUsers.rejected, (state: AdminUsersState) => {
            resetImport(state);
            state.import.state = ImportStates.Fail;
        });
        builder.addCase(confirmImport.pending, (state: AdminUsersState) => {
            state.import.state = ImportStates.Pending;
        });
        builder.addCase(
            confirmImport.fulfilled,
            (state: AdminUsersState, action) => {
                state.import.state = ImportStates.Importing;
                const data = action.payload.data;
                state.import.process = data;
                if (data.status === 'completed') {
                    state.import.state = ImportStates.Complete;
                }
            },
        );
        builder.addCase(confirmImport.rejected, (state: AdminUsersState) => {
            resetImport(state);
        });
        builder.addCase(cancelImport.pending, (state: AdminUsersState) => {
            state.import.state = ImportStates.Idle;
            state.import.importId = null;
        });
        builder.addCase(
            getImportProgress.fulfilled,
            (state: AdminUsersState, action) => {
                const data = action.payload.data;

                state.import.process = data;
                if (data.status === 'completed') {
                    state.import.state = ImportStates.Complete;
                }
            },
        );
        builder.addCase(
            getImportProgress.rejected,
            (state: AdminUsersState) => {
                resetImport(state);
            },
        );

        builder.addCase(LOGOUT, () => initialState);

        builder.addMatcher(
            isAnyOf(
                getUsers.pending,
                createUser.pending,
                updateUser.pending,
                deleteUser.pending,
            ),
            (state: AdminUsersState) => {
                state.users.loading = true;
            },
        );
        builder.addMatcher(
            isAnyOf(
                getUsers.fulfilled,
                createUser.fulfilled,
                updateUser.fulfilled,
                deleteUser.fulfilled,
                getUsers.rejected,
                createUser.rejected,
                updateUser.rejected,
                deleteUser.rejected,
            ),
            (state: AdminUsersState) => {
                state.users.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getUser.fulfilled, getUser.rejected),
            (state: AdminUsersState) => {
                state.detail.loading = false;
            },
        );
        builder.addMatcher(
            isAnyOf(getUser.pending, getUser.rejected),
            (state: AdminUsersState) => {
                state.detail.data = undefined;
            },
        );

        builder.addMatcher(
            isAnyOf(getReports.fulfilled, getReports.rejected),
            (state: AdminUsersState) => {
                state.reports.loading = false;
            },
        );
        builder.addMatcher(
            isAnyOf(getJobMatchReports.fulfilled, getJobMatchReports.rejected),
            (state: AdminUsersState) => {
                state.jobMatchReports.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getProjects.fulfilled, getProjects.rejected),
            (state: AdminUsersState) => {
                state.projects.loading = false;
            },
        );
        builder.addMatcher(
            isAnyOf(getManagedProjects.fulfilled, getManagedProjects.rejected),
            (state: AdminUsersState) => {
                state.managedProjects.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(getImportProgress.rejected, importUsers.rejected),
            (state: AdminUsersState) => {
                state.import.state = ImportStates.Idle;
            },
        );
    },
});

const state = (state: AppState) => state.admin.users;

const AdminUsersSlice = {
    reducer: slice.reducer,
    actions: {
        getUsers,
        createUser,
        updateUser,
        deleteUser,
        getUser,
        getReports,
        getJobMatchReports,
        getProjects,
        getManagedProjects,
        archive,
        anonymize,
        resetAuth,
        createJobMatch,
        importUsers,
        confirmImport,
        cancelImport,
        getImportProgress,
        exportUsers,
        getImportTemplate,
        ...slice.actions,
    },
    state: {
        all: createSelector(state, (state: AdminUsersState) => state),
        list: createSelector(state, (state: AdminUsersState) => state.users),
        detail: createSelector(state, (state: AdminUsersState) => state.detail),
        reports: createSelector(
            state,
            (state: AdminUsersState) => state.reports,
        ),
        jobMatchReports: createSelector(
            state,
            (state: AdminUsersState) => state.jobMatchReports,
        ),
        projects: createSelector(
            state,
            (state: AdminUsersState) => state.projects,
        ),
        managedProjects: createSelector(
            state,
            (state: AdminUsersState) => state.managedProjects,
        ),
        import: createSelector(state, (state: AdminUsersState) => state.import),
    },
};

function resetImport(state: AdminUsersState) {
    state.import.state = ImportStates.Idle;
    state.import.importId = null;
    state.import.preview = null;
    state.import.process = null;
}

export default AdminUsersSlice;
